import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "warehouse", "query", "results", "items", "item", "empty", "rawTotal", "discount",
    "discountAmount", "total", "paid", "change"
  ]

  static values = {
    searchUrl: String,
    noStockText: String,
    notEnoughStockText: String,
    removeText: String,
    removeIcon: String,
    quickSaleLabel: String,
    showPurchasePrice: Boolean
  }

  connect() {
    this.index = this.itemTargets.length
    this.timeout = null
    this.recalculate()
    this.syncEmptyState()
  }

  search() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchProducts(), 200)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  clearItems() {
    this.itemTargets.forEach((item) => item.remove())
    this.recalculate()
    this.syncEmptyState()
    this.hideResults()
  }

  removeItem(event) {
    event.currentTarget.closest("tr")?.remove()
    this.recalculate()
    this.syncEmptyState()
  }

  addQuickSaleFromEvent(event) {
    this.itemsTarget.append(this.buildQuickSaleItem(event.detail))
    this.recalculate()
    this.syncEmptyState()
  }

  recalculate() {
    let rawTotal = 0

    this.itemTargets.forEach((item) => {
      const quantity = this.numberValue(item.querySelector("input[data-role='quantity']")?.value)
      const price = this.numberValue(item.dataset.sellingPrice)
      const amount = quantity * price
      rawTotal += amount

      const amountTarget = item.querySelector("[data-role='amount']")
      if (amountTarget) amountTarget.textContent = this.formatAmount(amount)
    })

    const discount = this.numberValue(this.discountTarget.value)
    const discountAmount = rawTotal * discount / 100
    const total = rawTotal * (1 - discount / 100)
    const paid = this.numberValue(this.paidTarget.value)
    const change = Math.max(paid - total, 0)

    this.rawTotalTarget.textContent = this.formatAmount(rawTotal)
    if (this.hasDiscountAmountTarget) this.discountAmountTarget.textContent = this.formatAmount(discountAmount)
    this.totalTarget.textContent = this.formatAmount(total)
    this.changeTarget.textContent = this.formatAmount(change)
    this.changeTarget.classList.toggle("text-red-700", change > 0)
  }

  async fetchProducts() {
    const query = this.queryTarget.value.trim()
    const warehouseId = this.warehouseTarget.value
    if (query.length < 2 || warehouseId.length === 0) {
      this.hideResults()
      return
    }

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("query", query)
    url.searchParams.set("warehouse_id", warehouseId)

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok) {
      this.hideResults()
      return
    }

    const data = await response.json()
    if (data.exact_product) {
      this.addProduct(data.exact_product)
      return
    }

    this.renderResults(data.products || [])
  }

  renderResults(products) {
    this.resultsTarget.replaceChildren()
    if (products.length === 0) {
      this.hideResults()
      return
    }

    products.forEach((product) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-primary-50"
      button.innerHTML = `
        <span class="min-w-0">
          <span class="block truncate font-medium text-gray-900">${this.escape(product.title)}</span>
          <span class="block truncate text-xs text-gray-500">${this.escape(product.sku || "")}</span>
        </span>
        <span class="shrink-0 text-right text-xs text-gray-500">
          <span class="block">${this.escape(product.display_selling_price || "")}</span>
          <span class="block">${this.escape(String(product.on_hand))}</span>
        </span>
      `
      button.addEventListener("click", () => this.addProduct(product))
      this.resultsTarget.append(button)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  addProduct(product) {
    if (product.on_hand <= 0) {
      window.alert(this.noStockTextValue)
      return
    }

    const existingItem = this.itemTargets.find((item) => item.dataset.productId === String(product.id))
    if (existingItem) {
      this.incrementExistingItem(existingItem, product)
    } else {
      this.itemsTarget.append(this.buildStockItem(product))
    }

    this.queryTarget.value = ""
    this.hideResults()
    this.queryTarget.focus()
    this.recalculate()
    this.syncEmptyState()
  }

  incrementExistingItem(item, product) {
    const input = item.querySelector("input[data-role='quantity']")
    const currentQuantity = this.numberValue(input.value)
    if (currentQuantity >= product.on_hand) {
      window.alert(this.notEnoughStockTextValue)
      return
    }

    input.value = String(currentQuantity + 1)
  }

  buildStockItem(product) {
    const row = document.createElement("tr")
    row.dataset.retailOrderTarget = "item"
    row.dataset.productId = product.id
    row.dataset.onHand = product.on_hand
    row.dataset.sellingPrice = product.selling_price || "0"

    const index = this.index++
    row.innerHTML = this.stockRowHtml(product, index)
    return row
  }

  buildQuickSaleItem(item) {
    const row = document.createElement("tr")
    row.dataset.retailOrderTarget = "item"
    row.dataset.sellingPrice = item.sellingPrice || "0"

    const index = this.index++
    row.innerHTML = this.quickSaleRowHtml(item, index)
    return row
  }

  stockRowHtml(product, index) {
    return this.rowHtml({
      index,
      productId: product.id,
      title: product.title,
      sku: product.sku || "",
      stock: String(product.on_hand),
      quantity: "1",
      sellingPrice: product.selling_price || "0",
      displayPurchasePrice: product.display_purchase_price || "",
      displaySellingPrice: product.display_selling_price || "",
      isQuickSale: "0"
    })
  }

  quickSaleRowHtml(item, index) {
    return this.rowHtml({
      index,
      title: item.title,
      sku: this.quickSaleLabelValue,
      stock: this.quickSaleLabelValue,
      quantity: item.quantity || "1",
      sellingPrice: item.sellingPrice || "0",
      purchasePrice: item.purchasePrice || "0",
      warranty: item.warranty || "0",
      displayPurchasePrice: this.formatAmount(this.numberValue(item.purchasePrice || "0")),
      displaySellingPrice: this.formatAmount(this.numberValue(item.sellingPrice || "0")),
      isQuickSale: "1"
    })
  }

  rowHtml(data) {
    const cell = "h-10 py-2 text-sm px-3 text-gray-500"
    const centerCell = `${cell} text-center`
    const rightCell = `${cell} text-right`
    const primaryCell = "h-10 py-2 text-sm w-full max-w-0 pl-4 pr-3 sm:w-auto sm:max-w-none sm:pl-6 text-left"
    const actionCell = "h-10 py-1 px-3 text-right text-sm text-gray-500"
    const quantityInput = "block w-20 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-center text-sm leading-normal text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-0"
    const baseName = `retail_order[retail_order_items_attributes][${data.index}]`
    const hiddenProduct = data.productId ? `<input type="hidden" name="${baseName}[product_id]" value="${data.productId}">` : ""
    const quickFields = data.isQuickSale === "1" ? `
      <input type="hidden" name="${baseName}[title]" value="${this.escape(data.title)}">
      <input type="hidden" name="${baseName}[purchase_price]" value="${this.escape(data.purchasePrice)}">
      <input type="hidden" name="${baseName}[selling_price]" value="${this.escape(data.sellingPrice)}">
      <input type="hidden" name="${baseName}[warranty]" value="${this.escape(data.warranty)}">
    ` : ""
    const purchasePriceCell = this.showPurchasePriceValue ?
      `<td class="${rightCell}">${this.escape(data.displayPurchasePrice || "")}</td>` : ""

    return `
      <td class="${cell} text-left">${this.escape(data.sku)}</td>
      <td class="${primaryCell}">
        <span class="font-medium text-gray-900">${this.escape(data.title)}</span>
        ${hiddenProduct}
        ${quickFields}
        <input type="hidden" name="${baseName}[is_quick_sale]" value="${data.isQuickSale}">
      </td>
      <td class="${centerCell}">${this.escape(data.stock)}</td>
      ${purchasePriceCell}
      <td class="${rightCell}">${this.escape(data.displaySellingPrice)}</td>
      <td class="${centerCell}">
        <input class="${quantityInput}" data-role="quantity" data-action="input->retail-order#recalculate" inputmode="numeric" pattern="[0-9]*" name="${baseName}[quantity]" value="${this.escape(data.quantity)}">
      </td>
      <td class="${rightCell}" data-role="amount"></td>
      <td class="${actionCell}">${this.removeButtonHtml()}</td>
    `
  }

  removeButtonHtml() {
    const label = this.escape(this.removeTextValue)
    const iconUrl = this.escape(this.removeIconValue)

    return `
      <button class="inline-flex size-8 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 hover:text-red-700" type="button" title="${label}" aria-label="${label}" data-action="retail-order#removeItem">
        <span class="size-4 bg-current" style="-webkit-mask: url('${iconUrl}') center / contain no-repeat; mask: url('${iconUrl}') center / contain no-repeat;"></span>
      </button>
    `
  }

  syncEmptyState() {
    if (!this.hasEmptyTarget) return

    this.emptyTarget.classList.toggle("hidden", this.itemTargets.length > 0)
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.replaceChildren()
  }

  numberValue(value) {
    const normalized = String(value || "0").replace(",", ".")
    const number = Number.parseFloat(normalized)
    return Number.isFinite(number) ? number : 0
  }

  formatAmount(amount) {
    return amount.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
